import { Box, Flex, Text, Spinner, Tooltip } from '@chakra-ui/react';
import {
  faBookSparkles,
  faInfoCircle,
  faPen
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonText,
  Link,
  OutlineCard,
  Switch,
  Table,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { updateFocusIssueStatus } from 'src/api/mutations';
import { getFocusIsuue } from 'src/api/queries';
import FocusIssueModal from './FocusIssueModal';
import { FocusIssues } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import FocusIssueGuidelinesModal from './FocusIssueGuidelinesModal';

interface Props {
  guidelineLink: string | undefined;
}
const FocusIssue: React.FC<Props> = ({ guidelineLink }) => {
  const { journalId = '' } = useParams();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const [isOpenGuidelines, setIsOpenGuidelines] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<FocusIssues | null>(null);

  const updateFocusIssueMutation = updateFocusIssueStatus(journalId);
  const { data, isLoading } = useQuery({
    queryKey: ['focus-issue', { id: journalId }],
    queryFn: () => getFocusIsuue(journalId),
    retry: 0
  });
  const toggleModal = () => setIsOpen(prev => !prev);
  const toggleGuidelinesModal = () => setIsOpenGuidelines(prev => !prev);
  const onChange = async (focusIssueId: string, statusIssue: string) => {
    const status = statusIssue === 'open' ? 'closed' : 'open';
    const response = await updateFocusIssueMutation.mutateAsync({
      status,
      focusIssueId
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'The special issue status has been updated successfully'
      });
    } else {
      toast({
        variant: 'error',
        title: 'Unexpected error.'
      });
    }
  };
  const renderUpdateModal = (item: FocusIssues) => {
    setIsOpenEdit(prev => !prev);
    setCurrentItem(item);
  };
  const handleEditClose = () => {
    setIsOpenEdit(prev => !prev);
    setCurrentItem(null);
  };
  return (
    <>
      {isOpen && (
        <FocusIssueModal isOpen onClose={toggleModal} journalId={journalId} />
      )}
      {isOpenGuidelines && (
        <FocusIssueGuidelinesModal
          isOpen={isOpenGuidelines}
          onClose={toggleGuidelinesModal}
          journalId={journalId}
          guidelineLink={guidelineLink}
        />
      )}
      {isOpenEdit && currentItem && (
        <FocusIssueModal
          isOpen={isOpenEdit}
          onClose={handleEditClose}
          journalId={journalId}
          item={currentItem}
        />
      )}
      <OutlineCard borderless paddingX={4} paddingY={6}>
      <Flex flexDirection={'column'} gap={4}>
        <Flex justifyContent="space-between" marginTop={6}>
          <Text textStyle="headingSection">Special Issue</Text>
          {data?.data?.length ? (
            <ButtonText onClick={toggleModal}>Create Special Issue</ButtonText>
          ) : null}
        </Flex>
        <Text textStyle="textBody">
          Manuscripts submitted under these issues can be prioritized for review
          and publication.
        </Text>
        {isLoading ? (
          <Spinner />
        ) : !data?.data?.length ? (
          <Flex flexDirection={'column'} gap={2} alignItems="flex-start">
            <Flex
              width={'100%'}
              padding={8}
              justifyContent="center"
              background="neutralSecondaryFill"
              borderRadius="lg"
              fontSize="48px"
            >
              <FontAwesomeIcon icon={faBookSparkles} />
            </Flex>

            <Text textStyle="headingGroup" marginTop={4}>
              No Special Issue Created
            </Text>
            <Text textStyle="textBody">
              Currently, there are no special issues created for this journal.
              Admins can initiate special issues and set their availability.
            </Text>
            <Button variant="secondary" marginY={4} onClick={toggleModal}>
              Create Special Issue
            </Button>
          </Flex>
        ) : (
          <Box overflowX="auto" width="100%">
          <Box minWidth="700px">
          <Table>
            <Table.HeaderRow gap={2}>
              <Table.HeaderCell flex="5">Issue ID</Table.HeaderCell>
              <Table.HeaderCell flex="5">
                <Flex gap={2}>
                  <Text>Special Issue</Text>
                  <Tooltip
                    hasArrow
                    placement="top"
                    borderRadius={4}
                    label="Shows which special issue this journal can receive."
                  >
                    <Text color="actionSecondaryContent">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Text>
                  </Tooltip>
                </Flex>
              </Table.HeaderCell>
              <Table.HeaderCell flex="5">Issue URL</Table.HeaderCell>
              <Table.HeaderCell flex="2">Status</Table.HeaderCell>
              <Table.HeaderCell flex="2"></Table.HeaderCell>
            </Table.HeaderRow>
            <Table.Body>
              {data.data.map(item => (
                <Table.Row key={item.id} gap={2}>
                  <Table.RowCell flex="5" paddingY={2} isTruncated>
                    <Text isTruncated>{item.issueId}</Text>
                  </Table.RowCell>
                  <Table.RowCell flex="5" paddingY={2} isTruncated>
                    <Text isTruncated maxW={'240px'}>
                      {item.name}
                    </Text>
                  </Table.RowCell>
                  <Table.RowCell flex="5" paddingY={2} isTruncated>
                    <Link
                      variant="primary"
                      href={item.issueLink}
                      target="_blank"
                    >
                      <Text isTruncated maxW={'240px'}>
                        {item.issueLink}
                      </Text>
                    </Link>
                  </Table.RowCell>
                  <Table.RowCell flex="2" paddingY={2}>
                    <Flex gap={2} alignItems="center">
                      <Switch
                        isChecked={item.status === 'open'}
                        onChange={() => onChange(item.id, item.status)}
                      />
                      <Text textTransform="capitalize">{item.status}</Text>
                    </Flex>
                  </Table.RowCell>
                  <Table.RowCell paddingY={2} flex="2">
                    <ButtonText
                      leftIcon={<FontAwesomeIcon icon={faPen} />}
                      onClick={() => renderUpdateModal(item)}
                    >
                      Edit
                    </ButtonText>
                  </Table.RowCell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          </Box>
          </Box>
        )}

        {!!data?.data?.length && (
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            justifyContent={'space-between'}
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={2}
          >
            <Text paddingY={4}>
              You can{' '}
              <Link
                variant="primary"
                css={{
                  color: guidelineLink ? '' : 'gray !important',
                  textDecoration: guidelineLink ? '' : 'none !important',
                  cursor: guidelineLink ? '' : 'auto'
                }}
                href={guidelineLink}
                target="_blank"
              >
                view
              </Link>{' '}
              the common guidelines for the Special Issues in this journal.
            </Text>
            <ButtonText variant="secondary" onClick={toggleGuidelinesModal}>
              Manage common guidelines
            </ButtonText>
          </Flex>
        )}
      </Flex>
      </OutlineCard>
    </>
  );
};

export default FocusIssue;
